export const storeSearchLocally = (
  searchText: string,
  commonSearchesLength: number = 3,
  hasAlgoliaConsent: boolean = false,
) => {
  if (typeof window === "undefined") {
    return
  }

  // Persisting recent search terms is gated on OneTrust consent for the
  // configured Algolia category (default C0002 Performance). Without
  // consent we skip the write entirely; AlgoliaInsightsBootstrap is
  // responsible for clearing any pre-existing entries.
  if (!hasAlgoliaConsent) {
    return
  }

  let localSearches: [] | null

  //Get any locally stored searches and turn them into an array
  const localSearchJson = localStorage?.getItem("searches")

  if (localSearchJson != null) {
    localSearches = JSON.parse(localSearchJson)
  } else {
    localSearches = []
  }

  //Convert the search array into a set so that we prevent duplicates ...
  //... if we don't use a set we could end up with something like:
  //"Your Recent Searches"
  //"search"
  //"search"
  //"search"
  //We are also operating in reverse chronological order ...
  //... since sets always return the values in insertion order
  const searchSet =
    localSearches != null ? new Set(localSearches.reverse()) : new Set()

  // if(searchSet != null){
  //If the set already contains the incoming search text, remove it from the set ...
  //... this will maintain chronological order when we re-add it to the set ...
  //... since sets iterate in insertion order
  //... this process also prevents duplicates

  searchSet.delete(searchText)

  // }
  //Add the item to the set
  searchSet.add(searchText)
  //Create a new array with the new search text
  //Trim the array to match the total number of common searches (min 3 per design) ...
  //... taking the last x items (most recent)
  //Reverse the array so that the most recent is first
  const trimLength = commonSearchesLength < 3 ? 3 : commonSearchesLength

  const newSearches: any[] = [...searchSet]?.slice(-trimLength)?.reverse()

  // Remove short substrings from the newSearches array
  const filteredSearches = []

  for (let i = 0; i < newSearches.length; i++) {
    const current: string = newSearches[i]

    if (current?.length > 4) {
      filteredSearches.push(current)
    } else {
      let useVal = true

      for (let j = 0; j < newSearches.length; j++) {
        const compared = newSearches[j]

        if (compared.includes(current)) {
          useVal = false
        }
      }

      if (useVal) {
        filteredSearches.push(current)
      }
    }
  }

  // Update the local storage to exclude substrings
  localStorage.setItem("searches", JSON.stringify(filteredSearches))

  //Store the new array in local storage
  localStorage.setItem("searches", JSON.stringify(filteredSearches))
}

export const combineIndexHits = (resultsObj: any) => {
  const { results } = resultsObj || {}
  let parsedHits: any[] = []
  let parsedNbHits = 0

  results?.forEach(({ hits, nbHits }: { hits: any[]; nbHits: number }) => {
    parsedHits = [...hits, ...parsedHits]
    parsedNbHits += nbHits
  })

  return { parsedHits, parsedNbHits }
}

export function getLocaleFilter(locale: string, isStrict: boolean): string {
  const localeFilter =
    localeFilterList[locale as keyof typeof localeFilterList] ||
    `(locale:${locale})`
  if (isStrict) {
    return `${localeFilter} AND contentLocales:${locale}`
  }
  return localeFilter
}

export const localeFilterList = {
  en: `(locale:en-US OR locale:en OR locale:en-GB)`,
  "en-US": `(locale:en-US OR locale:en OR locale:en-GB)`,
  es: `(locale:es-ES OR locale:es OR locale:es-es OR locale:es-AR OR locale:es-ar)`,
  "es-ES": `(locale:es-ES OR locale:es OR locale:es-es OR locale:es-AR OR locale:es-ar)`,
  "es-AR": `(locale:es-ES OR locale:es OR locale:es-es OR locale:es-AR OR locale:es-ar)`,
  fr: `(locale:fr-FR OR locale:fr OR locale:fr-fr)`,
  "fr-FR": `(locale:fr-FR OR locale:fr OR locale:fr-fr)`,
  da: `(locale:da-da OR locale:da OR locale:da-da)`,
  de: `(locale:de-DE OR locale:de OR locale:de-de)`,
  "de-DE": `(locale:de-DE OR locale:de OR locale:de-de)`,
  it: `(locale:it-IT OR locale:it OR locale:it-it)`,
  "it-IT": `(locale:it-IT OR locale:it OR locale:it-it)`,
  "ja-jp": `(locale:ja-JP OR locale:ja OR locale:ja-jp)`,
  "ja-JP": `(locale:ja-JP OR locale:ja OR locale:ja-jp)`,
  no: `(locale:no-NO OR locale:no OR locale:no-no)`,
  "no-NO": `(locale:no-NO OR locale:no OR locale:no-no)`,
  nl: `(locale:nl-NL OR locale:nl OR locale:nl-nl)`,
  "nl-NL": `(locale:nl-NL OR locale:nl OR locale:nl-nl)`,
  pl: `(locale:pl-PL OR locale:pl OR locale:pl-pl)`,
  ru: `(locale:ru-ru OR locale:ru OR locale:ru-RU)`,
  "ru-RU": `(locale:ru-ru OR locale:ru OR locale:ru-RU)`,
  ko: `(locale:ko-KR OR locale:ko OR locale:ko-kr)`,
  "ko-kr": `(locale:ko-KR OR locale:ko OR locale:ko-kr)`,
  "ko-KR": `(locale:ko-KR OR locale:ko OR locale:ko-kr)`,
  "zh-cn": `(locale:zh-CN OR locale:zh OR locale:zh-cn)`,
  "zh-Hans": `(locale:zh-TW OR locale:zh OR locale:zh-Hans OR locale:zh-hant-tw OR locale:zh-hant-TW OR locale:zh-Hant-TW OR locale:zh-Hant)`,
  "zh-hant-tw": `(locale:zh-TW OR locale:zh OR locale:zh-Hans OR locale:zh-hant-tw OR locale:zh-hant-TW OR locale:zh-Hant-TW OR locale:zh-Hant)`,
  "zh-Hant-tw": `(locale:zh-TW OR locale:zh OR locale:zh-Hans OR locale:zh-hant-tw OR locale:zh-hant-TW OR locale:zh-Hant-TW OR locale:zh-Hant)`,
  "zh-Hant": `(locale:zh-TW OR locale:zh OR locale:zh-Hans OR locale:zh-hant-tw OR locale:zh-hant-TW OR locale:zh-Hant-TW OR locale:zh-Hant)`,
  "ru-ru": `(locale:ru-ru OR locale:ru OR locale:ru-RU)`,
  sv: `(locale:sv OR locale:SV OR locale:sv-SE)`,
  pt: `(locale:pt-BR OR locale:pt OR locale:pt-pt)`,
  "pt-BR": `(locale:pt-BR OR locale:pt OR locale:pt-pt)`,
  cs: `(locale:cs OR locale:cs-CS OR locale:cs-cs)`,
  fi: `(locale:fi-fi OR locale:fi OR locale:fi-FI)`,
}
